/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { type FC, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useState } from "react"
import { Button, DockPanel, DockPanelBody, DockPanelHeader } from "@atomic/ui-kit"
import { DOCK_OVERLAY_MAX_WIDTH_PX } from "./dock_breakpoint"
import { Atom, X, Eraser } from "lucide-react"
import { Outlet, useLocation, useNavigate } from "react-router"
import { AssistantChatView } from "../../containers/AssistantChatView"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip"
import { RunSelectionProvider } from "../../hooks/runSelectionContext"
import { theme } from "../../styles/theme/theme"
import { Sidebar } from "./Sidebar"
import { useNucleusShortcut } from "./useNucleusShortcut"
import { AccountMenu, FeedbackButton } from "./AccountControls"
import { ViewAsBanner } from "../../containers/ViewAs/ViewAsBanner"
import { RoleGuard } from "../../access/RoleGuard"
import { FeedbackModal } from "./FeedbackModal"
import { useAuth } from "../../hooks/useAuth"
import { isTrustedEmbedMessage } from "../../utils/trustedEmbedMessage"

const DOCK_WIDTH_KEY = "nucleus.dock.width";
const DOCK_WIDTH_DEFAULT = 416; // the old fixed 26rem
const DOCK_WIDTH_MIN = 320;
/** Never past 60% of the viewport (and 960px), so the page stays usable. */
const clampDockWidth = (px: number): number =>
  Math.round(Math.min(Math.max(px, DOCK_WIDTH_MIN), Math.min(960, window.innerWidth * 0.6)));

const layoutStyles = {
  root: css`
    height: 100vh;
    background-color: ${theme.colors.background};
    overflow: hidden;
  `,
  inner: css`
    display: flex;
    height: 100%;
  `,
  mainContent: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    position: relative;
  `,
  contentRow: (dockOpen: boolean, dockWidth: number, dragging: boolean) => css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) ${dockOpen ? `${dockWidth}px` : "0"};
    flex: 1;
    min-height: 0;
    /* No easing while the user drags the divider — the column must track the pointer. */
    transition: ${dragging ? "none" : "grid-template-columns 0.22s ease"};

    @media (max-width: ${DOCK_OVERLAY_MAX_WIDTH_PX}px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  pageContent: css`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    position: relative;
  `,
  /** The draggable divider on the dock's left edge (user-resizable width,
   *  remembered per browser). Hidden in the narrow overlay layout, where the
   *  dock is full-width anyway. */
  dockResizeHandle: (dragging: boolean) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: -4px;
    width: 8px;
    cursor: col-resize;
    z-index: 5;
    touch-action: none;
    &::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 3px;
      width: 2px;
      background: ${dragging ? theme.colors.nucleus : "transparent"};
      transition: background 0.15s;
    }
    &:hover::after { background: ${theme.colors.nucleus}; }

    @media (max-width: ${DOCK_OVERLAY_MAX_WIDTH_PX}px) {
      display: none;
    }
  `,
}

const getCurrentViewLabel = (pathname: string) => {
  // /data-hub itself redirects to /data; only its drill-downs still render.
  if (pathname.startsWith("/data-hub")) return "Input Data";
  if (pathname === "/data" || pathname.startsWith("/data/")) return "Data";
  if (pathname.startsWith("/demand/validation")) return "Demand validation";
  if (pathname.startsWith("/replenishment")) return "Replenishment";
  if (pathname.startsWith("/shipments/planning-console")) return "Planning Console";
  if (pathname.startsWith("/shipments/reporting")) return "Shipments Reporting";
  if (pathname.startsWith("/shipments/validation")) return "Shipment validation";
  if (pathname.startsWith("/shipments/configuration")) return "Shipment configuration";
  if (pathname.startsWith("/runs/history")) return "Run History";
  if (pathname.startsWith("/tracker")) return "Plan Tracker"
  if (pathname.startsWith("/allocation-engine")) return "Allocation Engine";
  if (pathname.startsWith("/docs")) return "Docs";
  if (pathname.startsWith("/workflows")) return "Workflows";
  return "Current view";
}

const DashboardLayout: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isNucleusDockOpen, setIsNucleusDockOpen] = useState(false);
  // User-resizable dock width (px); clamped so the page keeps room and the
  // dock never collapses below a usable chat width. A drag lasts for the page
  // it was made on: every new page load opens the dock at the default width.
  const [dockWidth, setDockWidth] = useState<number>(DOCK_WIDTH_DEFAULT);
  useEffect(() => {
    // widths remembered by earlier builds
    try { window.localStorage.removeItem(DOCK_WIDTH_KEY); } catch { /* ignore */ }
  }, []);
  const [dockDragging, setDockDragging] = useState(false);
  const onDockResizeStart = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    // Capture the pointer on the HANDLE: the content column is full of
    // iframes (reports, tracker), and without capture the drag dies the
    // moment the pointer crosses one — the iframe's document swallows the
    // pointermove stream. Capture routes every event back here regardless.
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* capture unsupported — window listeners below still work off-iframe */
    }
    const startX = e.clientX;
    const startWidth = dockWidth;
    setDockDragging(true);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev: PointerEvent) => {
      // The dock sits on the right edge: dragging left widens it.
      setDockWidth(clampDockWidth(startWidth + (startX - ev.clientX)));
    };
    const onUp = () => {
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      setDockDragging(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [dockWidth]);
  // Bumped on every open so AssistantChatView (mounted but hidden while the
  // dock is closed) knows to put the cursor in its prompt input.
  const [dockFocusRequest, setDockFocusRequest] = useState(0);
  // "Clear conversation" in the dock header: the chat view resets on each
  // bump (new conversation, empty history) and takes focus — the dock stays open.
  const [dockResetRequest, setDockResetRequest] = useState(0);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const openNucleusDock = useCallback(() => {
    setIsNucleusDockOpen(true);
    setDockFocusRequest((n) => n + 1);
  }, []);

  const currentViewLabel = useMemo(() => getCurrentViewLabel(location.pathname), [location.pathname]);

  // A `?prompt=` deep link is a Nucleus conversation starter: open the dock so
  // AssistantChatView (which consumes the param) is mounted and visible. The
  // retired /nucleus full page redirects here with `openNucleus` in the router
  // state (see routes.tsx), so old links land on the same dock.
  const openNucleusFromState = Boolean((location.state as { openNucleus?: boolean } | null)?.openNucleus);
  useEffect(() => {
    if (openNucleusFromState || new URLSearchParams(location.search).has("prompt")) {
      openNucleusDock();
    }
  }, [location.search, openNucleusFromState, openNucleusDock]);

  // Ctrl+Shift+P opens the dock ready to type. Gated on the gateway's admission
  // answer rather than on a role, and extracted so that gate has a test
  // (NUC-296).
  useNucleusShortcut(Boolean(user?.nucleusAdmitted), openNucleusDock);

  // A plan's applied "open page" step: the allocation embed posts
  // nucleus:navigate with a route the CORE-side allowlist resolved. Validate
  // again here — in-app relative paths only — then navigate.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Same origin + source pair the embed adapters check, so only a frame
      // this page actually rendered can drive the host's router. (F-13)
      if (!isTrustedEmbedMessage(e)) return;
      const d = e.data as
        | { type?: string; page?: string; params?: Record<string, string> }
        | null;
      if (d?.type !== "nucleus:navigate" || typeof d.page !== "string") return;
      if (!d.page.startsWith("/") || d.page.startsWith("//")) return;
      const qs = new URLSearchParams(d.params ?? {}).toString();
      navigate(qs ? `${d.page}?${qs}` : d.page);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate]);

  return (
    <RoleGuard>
      <RunSelectionProvider>
        <div css={layoutStyles.root}>
          <div css={layoutStyles.inner}>
            {/* The rail is the whole chrome — no header bar: the wordmark at its
                top, the feedback trigger and the account menu on its floor. */}
            <Sidebar
              isNucleusDockOpen={isNucleusDockOpen}
              onNucleusToggle={() =>
                isNucleusDockOpen ? setIsNucleusDockOpen(false) : openNucleusDock()
              }
              footer={
                <>
                  <FeedbackButton onSendFeedback={() => setIsFeedbackOpen(true)} />
                  <AccountMenu />
                </>
              }
            />
            <FeedbackModal
              open={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
            />
            <div css={layoutStyles.mainContent}>
              <ViewAsBanner />
              <div css={layoutStyles.contentRow(isNucleusDockOpen, dockWidth, dockDragging)}>
                <div css={layoutStyles.pageContent}>
                  <Outlet />
                </div>

                {/* The kit's dock column (positioned, and over the page on a narrow
                    viewport); the resize handle is this layout's own, anchored on it. */}
                <DockPanel hidden={!isNucleusDockOpen}>
                  <div
                    css={layoutStyles.dockResizeHandle(dockDragging)}
                    onPointerDown={onDockResizeStart}
                    onDoubleClick={() => {
                      setDockWidth(DOCK_WIDTH_DEFAULT);
                    }}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize Nucleus sidebar (double-click to reset)"
                    title="Drag to resize · double-click to reset"
                  />
                  <DockPanelHeader icon={<Atom size={18} aria-hidden />} title="Nucleus">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="tertiaryText"
                          size="iconXs"
                          onClick={() => {
                            setDockResetRequest((n) => n + 1);
                            setDockFocusRequest((n) => n + 1);
                          }}
                          aria-label="Clear conversation"
                        >
                          <Eraser size={16} aria-hidden />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={6}>Reset</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button type="button" variant="tertiaryText" size="iconXs" onClick={() => setIsNucleusDockOpen(false)} aria-label="Close Nucleus">
                          <X size={16} aria-hidden />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={6}>Close</TooltipContent>
                    </Tooltip>
                  </DockPanelHeader>

                  <DockPanelBody>
                    <AssistantChatView
                      currentViewLabel={currentViewLabel}
                      focusRequest={dockFocusRequest}
                      resetRequest={dockResetRequest}
                      open={isNucleusDockOpen}
                    />
                  </DockPanelBody>
                </DockPanel>
              </div>
            </div>
          </div>
        </div>
      </RunSelectionProvider>
    </RoleGuard>
  )
}

export default DashboardLayout
